
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getBooks } from "@/lib/books";
import { Book } from "@/types/book";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/AuthProvider";
import { useAuthModal } from "@/hooks/use-auth-modal";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface SearchBarProps {
  className?: string;
  fullWidth?: boolean;
  collapsed?: boolean;
}

export function SearchBar({ className, fullWidth = false, collapsed = false }: SearchBarProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  
  // Fetch all books
  const { data: allBooks = [] } = useQuery<Book[]>({
    queryKey: ['books-list'],
    queryFn: getBooks,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Filter books based on search query - exclude dev books (no bundle and not preview)
  const filteredBooks = allBooks.filter((book) => {
    // First filter: must be in a bundle OR be a preview book
    if (!book.bundle_id && !book.is_preview) return false;
    
    if (!searchQuery) return false;
    
    const query = searchQuery.toLowerCase();
    const titleMatch = book.title?.toLowerCase().includes(query);
    const referenceMovieMatch = book.reference_movie ? book.reference_movie.toLowerCase().includes(query) : false;
    const authorMatch = book.author?.toLowerCase().includes(query);
    
    return titleMatch || referenceMovieMatch || authorMatch;
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (!user) {
          openAuthModal("search");
          return;
        }
        if (collapsed) {
          // Navigate to search page with focus
          navigate("/v2");
        } else {
          setOpen((open) => !open);
        }
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [collapsed, navigate, user, openAuthModal]);

  const handleSelect = (bookId: number) => {
    const selectedBook = allBooks.find((book) => book.id === bookId);
    if (selectedBook) {
      navigate(`/read/${bookId}`);
      toast({
        title: "Book found",
        description: `Opening "${selectedBook.title}"`,
      });
    }
    setOpen(false);
  };

  const handleClick = () => {
    if (collapsed) {
      // Navigate to search page instead of opening dialog
      navigate("/v2");
    } else {
      setOpen(true);
    }
  };

  const handleBlur = () => {
    if (collapsed && !open) {
      setIsExpanded(false);
    }
  };

  const SearchDialogContent = () => (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <DialogTitle className="sr-only">Search Books</DialogTitle>
      <DialogDescription className="sr-only">
        Search through available books by title, author, or reference movie
      </DialogDescription>
      <CommandInput 
        placeholder="Search books..." 
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Books">
          {filteredBooks.map((book) => (
            <CommandItem
              key={book.id}
              onSelect={() => handleSelect(book.id)}
              className="flex items-center gap-3 cursor-pointer"
            >
              {book.cover_image_url ? (
                <img 
                  src={book.cover_image_url} 
                  alt={book.title} 
                  className="h-10 w-7 object-cover rounded"
                />
              ) : (
                <div className="h-10 w-7 bg-indigo-800/50 rounded flex items-center justify-center text-xs">
                  📚
                </div>
              )}
              <div>
                <p className="font-medium">{book.title}</p>
                {book.reference_movie && (
                  <p className="text-xs text-muted-foreground">
                    Based on: {book.reference_movie}
                  </p>
                )}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );

  if (collapsed) {
    return (
      <>
        <button
          onClick={handleClick}
          onBlur={handleBlur}
          className={`${
            isExpanded ? "w-64" : "w-[32px]"
          } h-[32px] px-2 rounded-full flex items-center justify-center gap-2 text-white/60 hover:text-white transition-all duration-300 ease-in-out ${className}`}
        >
          <Search className="w-4 h-4 flex-shrink-0" />
          {isExpanded && (
            <span className="text-[13px] font-medium tracking-tight whitespace-nowrap overflow-hidden">
              Search... (⌘K)
            </span>
          )}
        </button>

        {/* Only show dialog for non-collapsed version */}
        {!collapsed && <SearchDialogContent />}
      </>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        className={`${
          fullWidth ? "w-full" : "w-auto"
        } glass px-4 py-2 rounded-full flex items-center gap-2 border border-white/10 text-white/60 hover:text-white transition-colors ${className}`}
      >
        <Search className="w-4 h-4" />
        <span className="text-sm">Search... (⌘K)</span>
      </button>

      <SearchDialogContent />
    </>
  );
}
